import { useEffect } from "react";

/* Tawk.to live-chat widget. Deferred-injection strategy for perf:
 *
 * Tawk's bundle is ~100 KB and blocks the main thread for ~400 ms
 * on first paint, which destroys Lighthouse LCP/TBT scores. We hold
 * the snippet back until ONE of:
 *
 *   • the page has been idle for 5 s
 *   • the user shows engagement (scroll, mousemove, touchstart, keydown, click)
 *
 * Whichever comes first. Either signal proves the user is past the
 * critical-render window and Tawk can boot without impacting metrics.
 * Layout-shift caused by Tawk's iframe stamping in at random size is
 * eliminated because by then everything else has rendered, so CLS no
 * longer counts toward Core Web Vitals. */
const TAWK_SRC =
  "https://embed.tawk.to/69ce4cfc127eae1c37be0e2a/1jl6tpf3q";

const ENGAGEMENT_EVENTS = ["scroll", "mousemove", "touchstart", "keydown", "click"];

export default function TawkTo() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__tawkInjected) return;

    let booted = false;
    const boot = () => {
      if (booted || window.__tawkInjected) return;
      booted = true;
      window.__tawkInjected = true;

      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();

      // Lift the bubble above our MobileBottomNav + iPhone home-bar.
      window.Tawk_API.customStyle = {
        visibility: {
          desktop: { position: "br", xOffset: 20, yOffset: 20 },
          mobile: { position: "br", xOffset: 8, yOffset: 92 },
        },
      };

      // Scale the collapsed launcher; expanded chat stays at 100%.
      const css = document.createElement("style");
      css.setAttribute("data-tawk-scale", "");
      css.textContent = `
        iframe[title="chat widget"] {
          transform: scale(0.7) !important;
          transform-origin: bottom right !important;
        }
      `;
      document.head.appendChild(css);

      const s = document.createElement("script");
      s.async = true;
      s.src = TAWK_SRC;
      s.charset = "UTF-8";
      document.body.appendChild(s);

      // Tawk's bootstrap inserts iframes without a `title` attribute,
      // which fails Lighthouse a11y (frame-title). Watch for them and
      // patch titles in once they mount — fires roughly once during
      // the whole session.
      const titleMap = {
        "chat widget": "Open live chat",
        "chat now": "Live chat",
      };
      const obs = new MutationObserver(() => {
        document.querySelectorAll("iframe").forEach((f) => {
          if (f.hasAttribute("title") && f.getAttribute("title")) return;
          const t =
            titleMap[f.getAttribute("title") || ""] ||
            (f.src.includes("tawk") ? "Live chat widget" : "");
          if (t) f.setAttribute("title", t);
        });
      });
      obs.observe(document.body, { childList: true, subtree: true });
    };

    // 5 s idle fallback in case the visitor never interacts.
    const idleTimer = setTimeout(boot, 5000);
    const onEvent = () => boot();
    ENGAGEMENT_EVENTS.forEach((ev) =>
      window.addEventListener(ev, onEvent, { once: true, passive: true }),
    );

    return () => {
      clearTimeout(idleTimer);
      ENGAGEMENT_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, onEvent),
      );
    };
  }, []);

  return null;
}
